import { useCallback, useEffect, useRef, useState } from 'react'
import { populationsApi, type AudienceSummary } from '@/lib/api'
import { DEFAULT_TITLE_FONT } from '@/layouts/AudienceImportSessionLayout/components/AudienceMetadataForm'
import type { NarrativeCardDraft, NarrativeCardStatus } from '@digital-twins/shared-types'

interface StageFormValues {
  name: string
  tagline: string
  titleFont: string
  selectedCoverPhotoIndex: number
  tier: 'tier1' | 'tier2' | 'tier3'
}

export interface StageMetadata {
  tagline?: string
  titleFont?: string
  selectedCoverPhotoIndex?: number
  coverPhotoOptions?: Array<{ url: string; thumbnailUrl?: string }>
}

export type AudienceStageData = AudienceSummary & {
  stageMetadata?: StageMetadata | null
}

function normalizeTier(raw: string | null | undefined): 'tier1' | 'tier2' | 'tier3' {
  if (raw === 'tier2' || raw === 'premium') return 'tier2'
  if (raw === 'tier3') return 'tier3'
  return 'tier1'
}

function extractValues(audience: AudienceStageData | null): StageFormValues {
  const meta = audience?.stageMetadata
  return {
    name: audience?.name ?? '',
    tagline: meta?.tagline ?? '',
    titleFont: meta?.titleFont ?? DEFAULT_TITLE_FONT,
    selectedCoverPhotoIndex: meta?.selectedCoverPhotoIndex ?? 0,
    tier: normalizeTier(audience?.tier),
  }
}

export function useAudienceStageForm(audienceId: string | null) {
  const [audience, setAudience] = useState<AudienceStageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [values, setValues] = useState<StageFormValues>(() => extractValues(null))
  const [initialValues, setInitialValues] = useState<StageFormValues>(() => extractValues(null))
  const [isSaving, setIsSaving] = useState(false)
  const [isGeneratingCovers, setIsGeneratingCovers] = useState(false)
  const [coverPhotoOptionsState, setCoverPhotoOptionsState] = useState<Array<{ url: string; thumbnailUrl?: string }>>([])
  const coverPhotoOptionsInitialized = useRef(false)
  const [narrativeCardDraft, setNarrativeCardDraft] = useState<NarrativeCardDraft | null>(null)
  const [narrativeCardInitial, setNarrativeCardInitial] = useState<NarrativeCardDraft | null>(null)
  const narrativeCardStatus: NarrativeCardStatus | null = narrativeCardDraft ? 'ready' : null



  useEffect(() => {
    if (!audienceId) return
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const data = (await populationsApi.getAudienceUiDetail(
          audienceId,
        )) as AudienceStageData
        if (cancelled) return
        setAudience(data)
        const vals = extractValues(data)
        setValues(vals)
        setInitialValues(vals)
        const nc = (data.narrativeCard as NarrativeCardDraft) ?? null
        setNarrativeCardDraft(nc)
        setNarrativeCardInitial(nc)
        if (!coverPhotoOptionsInitialized.current) {
          setCoverPhotoOptionsState(data.stageMetadata?.coverPhotoOptions ?? [])
          coverPhotoOptionsInitialized.current = true
        }
      } catch {
        if (!cancelled) setError('Failed to load audience data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [audienceId])

  const update = useCallback(
    (field: keyof StageFormValues, value: string | number) => {
      setValues((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const reset = useCallback(
    (field: keyof StageFormValues) => {
      const original = initialValues[field]
      setValues((prev) => ({ ...prev, [field]: original }))
    },
    [initialValues],
  )

  const generateCoverPhotos = useCallback(async () => {
    if (!audienceId || isGeneratingCovers) return
    setIsGeneratingCovers(true)
    setError(null)
    try {
      const result = await populationsApi.generateCoverPhotos(audienceId)
      const options = result.coverPhotoOptions
      setCoverPhotoOptionsState(options)
      setValues((prev) => ({ ...prev, selectedCoverPhotoIndex: 0 }))
    } catch {
      setError('Failed to generate cover photos')
    } finally {
      setIsGeneratingCovers(false)
    }
  }, [audienceId, isGeneratingCovers])

  const updateNarrativeCard = useCallback((draft: NarrativeCardDraft) => {
    setNarrativeCardDraft(draft)
  }, [])

  const saveAll = useCallback(async () => {
    if (!audienceId) return
    setIsSaving(true)
    try {
      const { name, tier, ...stageFields } = values
      const payload: Parameters<typeof populationsApi.update>[1] = {
        name,
        tier,
        stageMetadata: stageFields,
      }
      if (narrativeCardDraft && JSON.stringify(narrativeCardDraft) !== JSON.stringify(narrativeCardInitial)) {
        payload.narrativeCard = narrativeCardDraft
      }
      await populationsApi.update(audienceId, payload)
      setInitialValues(values)
      setNarrativeCardInitial(narrativeCardDraft)
      setError(null)
    } catch {
      setError('Failed to save changes')
      throw new Error('Save failed')
    } finally {
      setIsSaving(false)
    }
  }, [audienceId, values, narrativeCardDraft, narrativeCardInitial])

  const isDirty =
    JSON.stringify(values) !== JSON.stringify(initialValues) ||
    JSON.stringify(narrativeCardDraft) !== JSON.stringify(narrativeCardInitial)
  const respondentCount = audience?.rowCount ?? 0

  return {
    audience,
    loading,
    error,
    setError,
    values,
    initialValues,
    isDirty,
    isSaving,
    isGeneratingCovers,
    coverPhotoOptions: coverPhotoOptionsState,
    respondentCount,
    narrativeCardDraft,
    narrativeCardStatus,
    update,
    reset,
    updateNarrativeCard,
    generateCoverPhotos,
    saveAll,
  }
}
